<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";

import { suggest } from "../../../js/api/suggester";

import CitySelectDesktop from "#/components/header/CitySelectDesktop.vue";
import CitySelectMobile from "#/components/header/CitySelectMobile.vue";

type City = {
  id: string;
  text: string;
};

const props = withDefaults(
  defineProps<{
    popularCities?: string;
  }>(),
  {
    popularCities: "[]",
  }
);

const popularCities = computed<City[]>(() => {
  try {
    return JSON.parse(props.popularCities);
  } catch (error) {
    console.error("popularCities parse error", error);
    return [];
  }
});

const FORM_NAME = "city_select_form";
const FORM_SELECT_NAME = "city_select_form[city]";
const query = ref<string>("");
const foundedCities = ref<City[]>([]);
const selectedCity = ref<City | null>(null);
const isSelecting = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isPaginationLoading = ref<boolean>(false);
type ScrollSource = "desktop" | "mobile";

const page = ref<number>(1);
const hasMore = ref<boolean>(true);
let debounceTimeout: ReturnType<typeof setTimeout> | null = null;

const hasSearched = computed<boolean>(() => {
  return Boolean(query.value.trim());
});

const mobileCities = computed<City[]>(() => {
  if (!query.value.trim()) {
    return popularCities.value;
  }

  return foundedCities.value;
});

const selectCity = (city: City): void => {
  isSelecting.value = true;
  selectedCity.value = city;

  requestAnimationFrame(() => {
    isSelecting.value = false;
  });
};

const changeCity = (): void => {
  if (!selectedCity.value) {
    return;
  }

  const form = document.querySelector<HTMLFormElement>(
    `form[name="${FORM_NAME}"]`
  );

  if (!form) {
    return;
  }

  const select = form.querySelector<HTMLSelectElement>(
    `select[name="${FORM_SELECT_NAME}"]`
  );

  if (!select) {
    return;
  }

  let option = select.querySelector<HTMLOptionElement>(
    `option[value="${selectedCity.value.id}"]`
  );

  if (!option) {
    option = document.createElement("option");

    option.value = selectedCity.value.id;
    option.text = selectedCity.value.text;
    option.selected = true;
    select.appendChild(option);
  }

  select.value = selectedCity.value.id;

  form.submit();
};

watch(query, (value: string): void => {
  if (isSelecting.value) {
    return;
  }

  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }

  if (value.trim()) {
    isLoading.value = true;
  }

  debounceTimeout = setTimeout(async () => {
    if (!value.trim()) {
      await loadDefaultCities();
      return;
    }

    try {
      page.value = 1;
      hasMore.value = true;

      const result = await suggest("city", value, page.value);

      foundedCities.value = result.items ?? [];

      if (!(result.items ?? []).length) {
        hasMore.value = false;
      }
    } catch (error) {
      console.error(error);

      foundedCities.value = [];
    } finally {
      isLoading.value = false;
    }
  }, 300);
});

const loadMoreCities = async (): Promise<void> => {
  if (isPaginationLoading.value || !hasMore.value) {
    return;
  }

  try {
    isPaginationLoading.value = true;
    page.value += 1;
    const result = await suggest("city", query.value, page.value);
    const newItems = result.items ?? [];
    if (!newItems.length) {
      hasMore.value = false;
      return;
    }

    foundedCities.value.push(...newItems);
  } catch (error) {
    console.error(error);
  } finally {
    isPaginationLoading.value = false;
  }
};

const handleDropdownScroll = async (
  event: Event,
  source: ScrollSource
): Promise<void> => {
  if (source === "mobile" && !query.value.trim()) {
    return;
  }

  const target = event.target as HTMLElement;

  const scrollBottom =
    target.scrollHeight - target.scrollTop - target.clientHeight;

  if (scrollBottom < 50) {
    await loadMoreCities();
  }
};

const initSelectedCity = (): void => {
  const form = document.querySelector<HTMLFormElement>(
    `form[name="${FORM_NAME}"]`
  );

  if (!form) {
    return;
  }

  const select = form.querySelector<HTMLSelectElement>(
    `select[name="${FORM_SELECT_NAME}"]`
  );

  if (!select) {
    return;
  }

  const selectedOption = select.options[select.selectedIndex];

  if (!selectedOption || !selectedOption.value) {
    return;
  }

  selectedCity.value = {
    id: selectedOption.value,
    text: selectedOption.text,
  };
};

const loadDefaultCities = async (): Promise<void> => {
  try {
    isLoading.value = true;

    page.value = 1;
    hasMore.value = true;

    const result = await suggest("city", "", page.value);

    foundedCities.value = result.items ?? [];

    if (!(result.items ?? []).length) {
      hasMore.value = false;
    }
  } catch (error) {
    console.error(error);

    foundedCities.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleDesktopFocus = async (): Promise<void> => {
  if (foundedCities.value.length || isLoading.value) {
    return;
  }

  await loadDefaultCities();
};

onMounted(() => {
  initSelectedCity();
});
</script>

<template>
  <div class="city-select">
    <div class="d-none">
      <slot />
    </div>

    <!-- Desktop -->
    <city-select-desktop
      class="city-select__desktop"
      :query="query"
      :founded-cities="foundedCities"
      :selected-city="selectedCity"
      :is-loading="isLoading"
      :is-pagination-loading="isPaginationLoading"
      :has-searched="hasSearched"
      @update:query="query = $event"
      @focus="handleDesktopFocus"
      @select-city="selectCity"
      @submit="changeCity"
      @scroll-dropdown="handleDropdownScroll($event, 'desktop')"
    />

    <!-- Mobile -->
    <city-select-mobile
      class="city-select__mobile"
      :cities="mobileCities"
      :query="query"
      :is-loading="isLoading"
      :is-pagination-loading="isPaginationLoading"
      :has-searched="hasSearched"
      @update:query="query = $event"
      @select-city="
        selectCity($event);
        changeCity();
      "
      @scroll-dropdown="handleDropdownScroll($event, 'mobile')"
    />

    <div class="popular-city-list mt-32">
      <span
        v-for="city in popularCities"
        :key="city.id"
        class="popular-city-list__item"
        @click="
          selectCity(city);
          changeCity();
        "
      >
        {{ city.text }}
      </span>
    </div>
  </div>
</template>

<style lang="scss">
.city-select {
  &__desktop {
    display: block;
  }

  &__mobile {
    display: none;
  }
}

.popular-city-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 32px;

  &__item {
    position: relative;
    padding: 4px 12px;
    background: #f7f7f9;
    border-radius: 12px;
    color: #1d1f25;
    font-family: Onest, sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    cursor: pointer;
    letter-spacing: 0;

    &:hover {
      color: #ff9800;
    }
  }
}

@media (max-width: 767px) {
  .city-select {
    &__desktop {
      display: none;
    }

    &__mobile {
      display: block;
    }
  }

  .popular-city-list {
    display: none;
  }
}
</style>
